/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from 'react';
import { Input } from 'components/common/Input/Input';
import { Button } from 'components/common/Button/Button';
import { useTranslation } from 'react-i18next';

interface OTPFormProps {
	onSubmit: (otp: string) => void;
	length?: number;
	disabled?: boolean;
	isLoading?: boolean;
	className?: string;
	error?: boolean;
}

const OTPForm: React.FC<OTPFormProps> = ({
	onSubmit,
	length = 6,
	disabled = false,
	isLoading = false,
	className = '',
	error
}) => {
	const { t } = useTranslation();
	const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const formRef = useRef<HTMLDivElement>(null);
	const isSubmittingRef = useRef(false);

	const handleSubmit = (otpArray: string[]): void => {
		// Prevent double submission
		if (isSubmittingRef.current) return;

		const otpString = otpArray.join('');
		if (otpString.length === length) {
			isSubmittingRef.current = true;
			onSubmit(otpString);
			// Reset the flag after a short delay
			setTimeout(() => {
				isSubmittingRef.current = false;
			}, 100);
		}
	};

	useEffect(() => {
		const handlePaste = (e: ClipboardEvent) => {
			e.preventDefault();
			const pastedData = e.clipboardData
				?.getData('text')
				.replace(/\D/g, '')
				.slice(0, length);

			if (pastedData && pastedData.length === length) {
				const newOtp = pastedData.split('');
				setOtp(newOtp);

				// Manually update each input's value
				newOtp.forEach((digit, index) => {
					const input = inputRefs.current[index];
					if (input) {
						const nativeInputValueSetter =
							Object.getOwnPropertyDescriptor(
								window.HTMLInputElement.prototype,
								'value'
							)?.set;
						if (nativeInputValueSetter) {
							nativeInputValueSetter.call(input, digit);
							input.dispatchEvent(
								new Event('input', { bubbles: true })
							);
						}
					}
				});

				inputRefs.current[length - 1]?.focus();
				handleSubmit(newOtp);
			}
		};

		const currentFormRef = formRef.current;
		currentFormRef?.addEventListener('paste', handlePaste);

		return () => {
			currentFormRef?.removeEventListener('paste', handlePaste);
		};
	}, [length]);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 1);
		if (!value && e.target.value) {
			return;
		}

		const newOtp = [...otp];
		newOtp[index] = value;
		setOtp(newOtp);

		if (value && index < length - 1) {
			inputRefs.current[index + 1]?.focus();
		}

		// Only submit if all digits are filled and it's not from a paste event
		if (
			!isSubmittingRef.current &&
			newOtp.every(val => val !== '') &&
			newOtp.join('').length === length
		) {
			handleSubmit(newOtp);
		}
	};

	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>,
		index: number
	): void => {
		if (
			!/^\d$/.test(e.key) &&
			!['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
		) {
			e.preventDefault();
		}

		if (e.key === 'Backspace') {
			if (!otp[index] && index > 0) {
				// If current input is empty and we're not at first input,
				// move to previous input
				inputRefs.current[index - 1]?.focus();
			} else {
				// Clear current input
				const newOtp = [...otp];
				newOtp[index] = '';
				setOtp(newOtp);
			}
		}
	};

	const handleInputPaste = (e: React.ClipboardEvent) => {
		e.preventDefault();
	};

	return (
		<div
			className={`mx-auto h-full w-full max-w-md px-0 md:px-6 ${className}`}
			ref={formRef}
		>
			<div className='align-center flex flex-col justify-center text-center'>
				<div className='flex justify-center gap-2'>
					{otp.map((digit, index) => (
						<Input
							key={index}
							ref={el => (inputRefs.current[index] = el)}
							type='text'
							value={digit}
							onChange={e => handleChange(e, index)}
							onKeyDown={e => handleKeyDown(e, index)}
							onPaste={handleInputPaste}
							className={`h-12 w-12 text-center text-xl ${error && 'border-red-500'} px-0`}
							maxLength={1}
							autoComplete='off'
							size='large'
							disabled={disabled || isLoading}
							aria-label={`Digit ${index + 1}`}
							inputMode='numeric'
							pattern='[0-9]*'
						/>
					))}
				</div>
				{error && (
					<p className='p-5 text-sm text-red-500'>
						{t('otp.invalid_otp')}
					</p>
				)}
				<div className='py-8'>
					<Button
						type='submit'
						isLoading={isLoading}
						onClick={() => handleSubmit(otp)}
					>
						{t('common.continue')}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default OTPForm;
